import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import DataTable from "../global/DataTable";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import CustomSelect, { optionType } from "../organism/CustomSelect";
import PatientSearch from "../global/PatientSearch";
import dataConstants from "../../constants/dataConstants";
import reducers from "../../services/reducers";
import { PAGINATION_ACTIONS } from "../../services/reducers/PaginationReducer";
import CustomMultiSelect from "../organism/CustomMultiSelect";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import TableLoadingIndicator from "../global/LoadingIndicators/TableLoadingIndicator";
import Button from "../organism/Button";
import useProfile from "../../services/utils/custom hooks/useProfile";
import { useHistory, useLocation } from "react-router-dom";
import CustomText from "../atomic/CustomText";
import usePickupList from "../../services/utils/custom hooks/usePickupList";
import InfoSliderDispatcher from "../molecular/InfoSliderDispatcherButton";
import useMobile from "../../utils/helpers/useMobile";


function PickupTable({
  tableContainerClass,
}: {
  tableContainerClass?: string;
}): JSX.Element {
  const location = useLocation();
  const {
    state: { selectedOrganization, selectedPhysician },
    dispatch: applicationStateDispatcher,
  } = useContext(ApplicationState);
  const [params, setParams] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: profileData } = useProfile();
  const isCOA = profileData?.user?.roles?.includes("Collaborative Order Agreement") && profileData?.user?.roles?.length === 1;
  const { data, isLoading, isError } = usePickupList({
    params,
    updatePagination: ({ totalPages }) => {
      dispatch({ type: "setPageCount", payload: totalPages });
    },
    sort: sorting?.[0],
  });
  const [orders, setOrders] = useState([]);
  const query = useQueryParams();
  const [statusDropDownOptions, setStatusDropDownOptions] = useState(
    (() => {
      const base = isCOA
        ? dataConstants.statusDropDownOptions.filter(
            (opt) => opt.label !== "Pending Approval"
          )
        : dataConstants.statusDropDownOptions;
      const statusValues = query.getAll("status[]");
      return base.map((option) => {
        if (statusValues.includes(option.value.toString())) {
          return { ...option, selected: true };
        }
        return option;
      });
    })()
  );
  const queryPagination = {
    page: query.get("page"),
    size: query.get("size"),
  };
  const [{ pageIndex, pageSize, pageCount }, dispatch]: any = useReducer<any>(
    reducers.paginationReducer,
    {
      pageIndex: queryPagination?.page ? Number(queryPagination.page) : 1,
      pageSize: queryPagination?.size ? Number(queryPagination?.size) : 15,
      pageCount: data?.data?.totalPages ? data.data.totalPages : -1,
    }
  );
  const columns = useMemo(() => dataConstants.pickupTableColumns, []);
  const history = useHistory();
  const isMobile = useMobile();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    columns
      .map((column: any) => ({
        id: column.accessorKey,
        value: query.get(column.accessorKey),
      }))
      .filter((data) => !!data.value)
  );

  const foundTestTypeFilterValue = columnFilters.find(
    (data) => data.id === "testTypeCode"
  )?.value;
  const pagination = {
    pageIndex,
    pageSize,
    pageCount,
    onNextPage,
    totalItems: data?.data?.totalItems ?? 0,
    onPreviousPage,
    setPage,
    setPageSize,
  };
  const isDocuments = location.pathname.includes("/access/documents");

  function onNextPage() {
    dispatch({ type: PAGINATION_ACTIONS.INCREMENT_PAGE });
  }

  function setPage(page: number) {
    dispatch({ type: PAGINATION_ACTIONS.SET_PAGE, payload: page });
  }

  function setPageSize(size: number) {
    dispatch({ type: PAGINATION_ACTIONS.UPDATE_PAGE_SIZE, payload: size });
  }

  function onPreviousPage() {
    dispatch({ type: PAGINATION_ACTIONS.DECREMENT_PAGE });
  }

  function handleTestTypeFilter(option: optionType) {
    setColumnFilters((old) => {
      let temp = [...old];
      const foundIndex = temp.findIndex(
        (data: any) => data.id === "testTypeCode"
      );

      if (foundIndex !== -1) {
        if (temp[foundIndex]?.value !== option?.value) {
          if (option.value === "") {
            temp = temp.filter((data, index) => index !== foundIndex);
          } else
            temp[foundIndex] = { ...temp[foundIndex], value: option.value };
        } else {
          temp = temp.filter((data, index) => index !== foundIndex);
        }
      } else {
        temp.push({ id: "testTypeCode", value: option.value });
      }

      return temp;
    });
  }

  function handleStatusFilter(option: optionType) {
    setStatusDropDownOptions((prevState) => {
      let temp = [...prevState];
      let foundIndex = temp.findIndex((item) => item.value == option.value);

      if (foundIndex !== -1)
        temp[foundIndex].selected = !temp[foundIndex].selected;

      return temp;
    });
  }

  function handleClearFilters() {
    setColumnFilters([]);
    setStatusDropDownOptions((prevState) =>
      prevState.map((option) => ({ ...option, selected: false }))
    );
    if (
      profileData?.user?.roles?.includes?.("Physician") &&
      profileData?.user?.roles?.length === 1
    ) {
      applicationStateDispatcher({ type: "SET_ORGANIZATION", payload: null });
      applicationStateDispatcher({
        type: "SET_ORGANIZATION_DATA",
        payload: null,
      });
    } else applicationStateDispatcher({ type: "RESET" });
  }

  useEffect(() => {
    if (!isLoading) {
      setOrders(data?.data?.fedexPickups ?? []);
    }
  }, [isLoading, data]);

  if (isLoading && orders.length === 0) {
    return <TableLoadingIndicator />;
  }

  return (
    <div className="px-3 md:px-5">
      <div className="flex justify-start items-center mb-6">
        <div className="flex justify-start items-center w-full">
          <CustomText
            text="Pick-ups"
            fontWeight="font-semibold"
            fontSize="text-md"
            className=""
          />

          <InfoSliderDispatcher
            infoQuery={dataConstants.infoSliderQuery.pickups}
          />
        </div>
        {isMobile && (
          <Button
            textInformation={{
              text: "Schedule a pickup",
              fontWeight: "font-semibold",
            }}
            buttonTheme="primary"
            onClick={() => history.push("/access/schedule-pickup")}
            type="button"
            disabled={!selectedOrganization}
            tooltipText="Please select an organization first."
            className="block md:hidden "
            showTooltip={!selectedOrganization}
          />
        )}
      </div>
      <DataTable
        tableContainerClass={`2xl:h-[55vh] xl:h-[45vh] lg:h-[40vh] md:h-[30vh] overflow-auto border relative ${tableContainerClass}`}
        globalFilters={{
          status: isDocuments
            ? [{ label: "documents", value: 95, selected: true }]
            : statusDropDownOptions.filter((option) => option.selected),
          organizationId: selectedOrganization,
        }}
        pagination={pagination}
        data={orders}
        setParams={setParams}
        isFetching={isLoading && orders.length > 0}
        columns={columns}
        enableClientSideFiltering={false}
        enableClientSideSorting={false}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        // rowClickable={true}
        columnVisibility={{
          id: false,
          size: false,
          page: false,
          testTypeCode: false,
        }}
        manualSorting={{ sorting, setSorting }}
      />
    </div>
  );
}

export default PickupTable;
